import { useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, Text, View } from "react-native";
import {
  assembleWorkout,
  type BodyFigure,
  type BodyMapValue,
  type Exercise,
  type ProgramModel,
  type SessionResult,
  type UnitsPreference,
  type Workout,
  type WorkoutEnergyReadout,
  type WorkoutNote,
} from "@heraion/shared";
import {
  BodyMap,
  Button,
  CaloriesEffortBlock,
  SaveSessionAsModelDialog,
  SessionSummaryView,
  type SessionResultState,
  type UiTheme,
} from "@heraion/ui";

import { apiFetch } from "./api";
import { notifyViaWindow } from "./confirm";
import { durableWrite } from "./durableWrite";
import { useWebShareCard } from "./logger/useWebShareCard";
import { fetchSessionDetail } from "./useSessionLoader";

// The Workouts detail pane's body, split out of WorkoutsScreen.tsx to keep
// it under the file-size cap (prompt.md §Limiti di dimensione). Four states:
// no selection / loading / error / the loaded summary, with Edit, «Save as
// model» (#359) and Share.
export type WorkoutDetailPaneProps = {
  theme: UiTheme;
  dark: boolean;
  locale: string;
  unit: UnitsPreference;
  selectedId: string | null;
  detail: SessionResultState;
  /** The session's timeline notes (#24). */
  notes: readonly WorkoutNote[];
  resolveName: (exerciseId: string, snapshot?: string) => string;
  onOpenEdit: (sessionId: string) => Promise<void>;
  openingEditId: string | null;
  /** Which exercises are bodyweight, for «Save as model». */
  catalog: Exercise[] | null;
  /** «Plan a program» after «Save as model». Absent: no such action. */
  onPlanFromModel?: (model: Omit<ProgramModel, "id">) => void;
  bodyMapValue: BodyMapValue | null;
  figure?: BodyFigure;
  energyReadout: WorkoutEnergyReadout | null;
  t: (key: string, opts?: Record<string, unknown>) => string;
};

export function WorkoutDetailPane({
  theme,
  dark,
  locale,
  unit,
  selectedId,
  detail,
  notes,
  resolveName,
  onOpenEdit,
  openingEditId,
  catalog,
  onPlanFromModel,
  bodyMapValue,
  figure,
  energyReadout,
  t,
}: WorkoutDetailPaneProps) {
  if (selectedId === null) {
    return (
      <View style={styles.center}>
        <Text style={[styles.prompt, { color: theme.palette.ink2 }]}>
          {t("mobile.workouts.selectPrompt")}
        </Text>
      </View>
    );
  }
  if (detail.result === null && detail.status === "loading") {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={theme.palette.ink2} />
      </View>
    );
  }
  if (detail.result === null) {
    return (
      <View style={styles.center}>
        <Text style={[styles.prompt, { color: theme.palette.danger }]}>
          {t("mobile.workouts.loadError")}
        </Text>
      </View>
    );
  }
  return (
    <ScrollView contentContainerStyle={styles.detailScroll}>
      <LoadedSummary
        theme={theme}
        dark={dark}
        locale={locale}
        unit={unit}
        sessionId={selectedId}
        result={detail.result}
        notes={notes}
        resolveName={resolveName}
        onOpenEdit={onOpenEdit}
        openingEditId={openingEditId}
        catalog={catalog}
        onPlanFromModel={onPlanFromModel}
        bodyMapValue={bodyMapValue}
        figure={figure}
        energyReadout={energyReadout}
        t={t}
      />
    </ScrollView>
  );
}

// The loaded summary, its own component so the share hook only runs with a
// result in hand (#318: the share sits under the summary, as after a save).
function LoadedSummary({
  theme,
  dark,
  locale,
  unit,
  sessionId,
  result,
  notes,
  resolveName,
  onOpenEdit,
  openingEditId,
  catalog,
  onPlanFromModel,
  bodyMapValue,
  figure,
  energyReadout,
  t,
}: Omit<WorkoutDetailPaneProps, "selectedId" | "detail"> & {
  sessionId: string;
  result: SessionResult;
}) {
  const { sharing, onShare, capture } = useWebShareCard(undefined, {
    result,
    energy: energyReadout,
    locale,
    unit,
    resolveName,
  });
  // The sets are read on the tap, as Edit does. Keyed by session: the
  // selection can move while the read runs.
  const [model, setModel] = useState<{ sessionId: string; workout: Workout } | null>(null);
  const [readingModel, setReadingModel] = useState(false);
  const openModel = async () => {
    setReadingModel(true);
    try {
      const res = await fetchSessionDetail(sessionId);
      const workout = assembleWorkout(res.session, res.sets);
      if (workout.entries.length === 0) throw new Error("no entries");
      setModel({ sessionId, workout });
    } catch (e) {
      console.warn("[workouts] save_model_read", e instanceof Error ? e.message : e);
      notifyViaWindow(t("mobile.workouts.loadError"), t("errors.generic"));
    } finally {
      setReadingModel(false);
    }
  };
  return (
    <>
      <SessionSummaryView
        theme={theme}
        result={result}
        locale={locale}
        unit={unit}
        resolveName={resolveName}
        notes={notes}
        actionSlot={
          <View style={[styles.actions, { gap: theme.spacing.s2 }]}>
            <Button
              theme={theme}
              variant="secondary"
              icon="edit"
              label={t("workout.edit")}
              onPress={() => void onOpenEdit(sessionId)}
              loading={openingEditId === sessionId}
              // Button's 40pt box relies on Pressable's hitSlop to reach the
              // 44pt floor (packages/ui/src/primitives/Button.tsx:17-18):
              // that's RN-native behaviour; react-native-web's Pressable
              // doesn't implement hitSlop at all, so on this surface the
              // box itself must be 44 (a11y audit, 2026-08-18).
              style={styles.actionButton}
            />
            <Button
              theme={theme}
              variant="secondary"
              icon="save"
              label={t("library.saveAsModel")}
              numberOfLines={2}
              onPress={() => void openModel()}
              loading={readingModel}
              style={styles.actionButton}
            />
          </View>
        }
        bodyMapSlot={
          bodyMapValue && Object.keys(bodyMapValue).length > 0 ? (
            <BodyMap
              mode="display"
              view="front"
              value={bodyMapValue}
              theme={theme}
              isDark={dark}
              figure={figure}
              layout="dual"
            />
          ) : undefined
        }
        energySlot={
          energyReadout ? (
            <CaloriesEffortBlock theme={theme} {...energyReadout} />
          ) : undefined
        }
        shareSlot={
          <Button
            theme={theme}
            label={t("mobile.postSession.shareCta", {
              defaultValue: "Share session card",
            })}
            numberOfLines={2}
            onPress={() => void onShare()}
            loading={sharing}
            style={[styles.shareButton, { marginTop: theme.spacing.s2 }]}
          />
        }
      />
      {capture}
      {model?.sessionId === sessionId ? (
        <SaveSessionAsModelDialog
          theme={theme}
          workout={model.workout}
          defaultName={model.workout.name ?? t("mobile.workouts.fallbackName")}
          catalog={catalog}
          unit={unit}
          apiFetch={apiFetch}
          durableWrite={durableWrite}
          resolveName={resolveName}
          onPlanFromModel={onPlanFromModel}
          onClose={() => setModel(null)}
        />
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, alignItems: "center", justifyContent: "center", padding: 24 },
  prompt: { fontSize: 14, textAlign: "center" },
  detailScroll: { padding: 16 },
  actions: { flexDirection: "row", flexWrap: "wrap" },
  // flexGrow: wrapped buttons take the full width, one line shares it.
  actionButton: { minHeight: 44, flexGrow: 1 },
  // Same 44 floor as actionButton: RNW ignores hitSlop.
  shareButton: { minHeight: 44 },
});
